import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Input } from '@cortex/web-design-system/components/ui/Input';
import { useUpdateProjectDeadlines } from '../../../hooks/usePmoBoard.js';
import type { PmoOverdueProject, PmoDeadlineUpdateInput } from '../../../types/project.js';

interface DeadlineEditModalProps {
  open: boolean;
  onClose: () => void;
  project: PmoOverdueProject | null;
}

/**
 * ISO 8601 タイムスタンプ（例: 2026-04-01T00:00:00Z）から日付部分（YYYY-MM-DD）を抜き出す
 * @graph-connects none
 */
function toDateInputValue(value: string | null): string {
  if (!value) return '';
  return value.slice(0, 10);
}

/**
 * 編集前後で変更がある期限フィールドだけを抽出して PATCH ペイロードを構築する。
 * 空文字 → Backlog 側でクリアされる。
 * @graph-connects none
 */
function buildUpdatePayload(
  original: PmoOverdueProject,
  form: {
    integrationDeadline: string;
    integrationCompletedAt: string;
    systemTestDeadline: string;
    releaseDeadline: string;
    expectedReleaseDeadline: string;
  },
): PmoDeadlineUpdateInput {
  const payload: PmoDeadlineUpdateInput = {};
  if (form.integrationDeadline !== toDateInputValue(original.dueDate)) {
    payload.integrationDeadline = form.integrationDeadline;
  }
  if (form.integrationCompletedAt !== toDateInputValue(original.integrationCompletedAt)) {
    payload.integrationCompletedAt = form.integrationCompletedAt;
  }
  if (form.systemTestDeadline !== toDateInputValue(original.systemTestDeadline)) {
    payload.systemTestDeadline = form.systemTestDeadline;
  }
  if (form.releaseDeadline !== toDateInputValue(original.releaseDeadline)) {
    payload.releaseDeadline = form.releaseDeadline;
  }
  if (form.expectedReleaseDeadline !== toDateInputValue(original.expectedReleaseDeadline)) {
    payload.expectedReleaseDeadline = form.expectedReleaseDeadline;
  }
  return payload;
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PMO看板の期限編集モーダル。結合期限・結合完了日・総合期限・リリース期限・リリース予定日を編集し、変更があったフィールドのみBacklog本体へPATCHで書き戻す
 * @graph-connects useUpdateProjectDeadlines [calls] 期限更新ミューテーション
 */
export function DeadlineEditModal({ open, onClose, project }: DeadlineEditModalProps) {
  const { t } = useTranslation();
  const mutation = useUpdateProjectDeadlines();

  const [form, setForm] = useState({
    integrationDeadline: '',
    integrationCompletedAt: '',
    systemTestDeadline: '',
    releaseDeadline: '',
    expectedReleaseDeadline: '',
  });

  // 直前に初期化したプロジェクトの issueKey を覚えておき、別の PJ に切り替わったときだけフォームをリセットする。
  // useMutation() は毎レンダーで新しい object 参照を返すので、deps に含めると useEffect が毎回走り
  // ユーザーが選んだ日付がリセットされてしまうので入れない。
  const initializedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!project) {
      initializedForRef.current = null;
      return;
    }
    if (initializedForRef.current === project.issueKey) return;
    initializedForRef.current = project.issueKey;
    setForm({
      integrationDeadline: toDateInputValue(project.dueDate),
      integrationCompletedAt: toDateInputValue(project.integrationCompletedAt),
      systemTestDeadline: toDateInputValue(project.systemTestDeadline),
      releaseDeadline: toDateInputValue(project.releaseDeadline),
      expectedReleaseDeadline: toDateInputValue(project.expectedReleaseDeadline),
    });
    mutation.reset();
  }, [project, mutation]);

  if (!project) return null;

  const handleSave = () => {
    const payload = buildUpdatePayload(project, form);
    if (Object.keys(payload).length === 0) {
      onClose();
      return;
    }
    mutation.mutate(
      { issueKey: project.issueKey, body: payload },
      {
        onSuccess: () => {
          onClose();
        },
      },
    );
  };

  const setField = (key: keyof typeof form, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <Modal
      open={open}
      onClose={mutation.isPending ? () => undefined : onClose}
      title={t('pmoBoard.editTitle', { issueKey: project.issueKey })}
      description={project.summary}
      size="lg"
      // 5フィールドを2列で並べるため Modal のデフォルト size より広く取る。
      // Modal は className を cn(twMerge) でマージするため、後勝ちで max-w-lg を max-w-3xl に上書きできる。
      className="max-w-3xl"
      footer={
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="text-xs text-[var(--color-text-muted)] flex-1 min-w-0">
            {t('pmoBoard.editHint')}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
              {t('pmoBoard.cancel')}
            </Button>
            <Button variant="primary" onClick={handleSave} loading={mutation.isPending}>
              {t('pmoBoard.save')}
            </Button>
          </div>
        </div>
      }
    >
      {mutation.isError && (
        <div className="mb-4 p-3 rounded-lg bg-[var(--color-error)]/10 border border-[var(--color-error)]/30 text-sm text-[var(--color-error)]">
          {mutation.error instanceof Error ? mutation.error.message : t('pmoBoard.updateFailed')}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <DeadlineField
          label={t('pmoBoard.fields.integrationDeadline')}
          value={form.integrationDeadline}
          onChange={(v) => {
            setField('integrationDeadline', v);
          }}
        />
        <DeadlineField
          label={t('pmoBoard.fields.integrationCompletedAt')}
          value={form.integrationCompletedAt}
          onChange={(v) => {
            setField('integrationCompletedAt', v);
          }}
        />
        <DeadlineField
          label={t('pmoBoard.fields.systemTestDeadline')}
          value={form.systemTestDeadline}
          onChange={(v) => {
            setField('systemTestDeadline', v);
          }}
        />
        <DeadlineField
          label={t('pmoBoard.fields.releaseDeadline')}
          value={form.releaseDeadline}
          onChange={(v) => {
            setField('releaseDeadline', v);
          }}
        />
        <DeadlineField
          label={t('pmoBoard.fields.expectedReleaseDeadline')}
          value={form.expectedReleaseDeadline}
          onChange={(v) => {
            setField('expectedReleaseDeadline', v);
          }}
        />
      </div>

      <div className="mt-6 pt-4 border-t border-[var(--color-border)] text-xs text-[var(--color-text-muted)] space-y-1">
        <div>
          {t('pmoBoard.fields.assignee')}: {project.assignee ?? '-'}
        </div>
        <div>
          {t('pmoBoard.fields.mainWorker')}: {project.mainWorker ?? '-'}
        </div>
        <div>
          {t('pmoBoard.fields.devLeader')}: {project.devLeader ?? '-'}
        </div>
      </div>
    </Modal>
  );
}

interface DeadlineFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
}

/** @graph-connects none */
function DeadlineField({ label, value, onChange }: DeadlineFieldProps) {
  return (
    <div>
      <label className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1">
        {label}
      </label>
      <Input
        type="date"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        size="md"
      />
    </div>
  );
}
